const EXCLUDED_KEYS = new Set([
  'x',
  'y',
  'x0',
  'x1',
  'y0',
  'y1',
  'cx',
  'cy',
  'r',
  'width',
  'height',
  'index',
  'color',
  'fill',
  'stroke',
  'path',
  'points',
  'sourceLinks',
  'targetLinks',
  'vx',
  'vy',
  'depth',
  'children',
  'parent',
])

function isPrimitive(value) {
  return (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value instanceof Date
  )
}

function formatValue(value) {
  if (value instanceof Date) {
    return value.toLocaleString()
  }
  if (value === null || value === undefined || value === '') {
    return '—'
  }
  return String(value)
}

function buildRowsFromEntries(entries) {
  return entries.map(([label, value]) => ({
    label,
    value: formatValue(value),
  }))
}

function uniq(values) {
  return Array.from(new Set(values))
}

function isNumericLike(value) {
  return typeof value === 'number' && Number.isFinite(value)
}

function isDateLike(value) {
  return value instanceof Date && !Number.isNaN(value.valueOf())
}

function getOriginalFieldEntries(datum, dataTypes) {
  return Object.keys(dataTypes || {})
    .filter((key) => datum[key] !== undefined)
    .map((key) => [key, datum[key]])
}

function getFallbackEntries(datum) {
  return Object.entries(datum).filter(
    ([key, value]) =>
      !key.startsWith('_') &&
      !EXCLUDED_KEYS.has(key) &&
      isPrimitive(value)
  )
}

function getCommonArrayEntries(items, dataTypes) {
  const first = items[0]
  const candidateEntries = getOriginalFieldEntries(first, dataTypes).filter(
    ([, value]) => isPrimitive(value)
  )

  return candidateEntries.filter(([key, value]) =>
    items.every((item) => item?.[key] === value)
  )
}

function summarizeArrayField(items, key) {
  const values = items
    .map((item) => item?.[key])
    .filter((value) => value !== undefined && value !== null && value !== '')

  if (!values.length) return null

  if (values.every(isNumericLike)) {
    const min = Math.min(...values)
    const max = Math.max(...values)
    return [key, min === max ? min : `${min} to ${max}`]
  }

  if (values.every(isDateLike)) {
    const timestamps = values.map((value) => value.valueOf())
    const min = new Date(Math.min(...timestamps))
    const max = new Date(Math.max(...timestamps))
    return [key, min.valueOf() === max.valueOf() ? min : `${formatValue(min)} to ${formatValue(max)}`]
  }

  const uniqueValues = uniq(values.map((value) => String(value)))
  if (uniqueValues.length === 1) {
    return [key, uniqueValues[0]]
  }
  if (uniqueValues.length <= 3) {
    return [key, uniqueValues.join(', ')]
  }

  return [key, `${uniqueValues.length} values`]
}

function getArraySummaryEntries(items, dataTypes) {
  const first = items[0]
  const candidateKeys = Object.keys(dataTypes || {}).filter(
    (key) => first?.[key] !== undefined
  )

  const commonEntries = getCommonArrayEntries(items, dataTypes)
  const commonKeys = new Set(commonEntries.map(([key]) => key))
  const summaryEntries = candidateKeys
    .filter((key) => !commonKeys.has(key))
    .map((key) => summarizeArrayField(items, key))
    .filter(Boolean)
    .slice(0, 4)

  return [...commonEntries, ...summaryEntries]
}

function getHierarchicalEntries(datum, dataTypes) {
  const rows = []
  const originalEntries = getOriginalFieldEntries(datum, dataTypes)
  if (originalEntries.length) {
    rows.push(...originalEntries)
  } else {
    if (datum.name !== undefined) {
      rows.push(['Name', datum.name])
    }
    if (datum.id !== undefined) {
      rows.push(['ID', datum.id])
    }
  }

  if (datum.value !== undefined && datum.value !== null) {
    rows.push(['Value', datum.value])
  }
  if (Array.isArray(datum.children)) {
    rows.push(['Children', datum.children.length])
  }
  if (datum.depth !== undefined && datum.depth !== null) {
    rows.push(['Level', datum.depth])
  }

  return rows
}

export function summarizeChartDatum(datum, dataTypes) {
  if (datum === null || datum === undefined) {
    return null
  }

  if (Array.isArray(datum)) {
    if (datum.length === 0) {
      return null
    }

    if (datum.length === 1) {
      return summarizeChartDatum(datum[0], dataTypes)
    }

    const summaryEntries = getArraySummaryEntries(datum, dataTypes)
    return {
      title: 'Selected series',
      rows: [
        { label: 'Records', value: String(datum.length) },
        ...buildRowsFromEntries(summaryEntries),
      ],
    }
  }

  if (typeof datum !== 'object') {
    return {
      title: 'Selected value',
      rows: [{ label: 'Value', value: formatValue(datum) }],
    }
  }

  const hierarchicalEntries =
    Array.isArray(datum.children) || datum.value !== undefined || datum.depth !== undefined
      ? getHierarchicalEntries(datum, dataTypes)
      : []
  const originalEntries = hierarchicalEntries.length
    ? hierarchicalEntries
    : getOriginalFieldEntries(datum, dataTypes)
  const fallbackEntries = getFallbackEntries(datum)
  const entries = originalEntries.length > 0 ? originalEntries : fallbackEntries

  if (!entries.length) {
    return null
  }

  return {
    title: 'Selected data point',
    rows: buildRowsFromEntries(entries),
  }
}

export function findChartDatumFromNode(targetNode, svgNode) {
  if (!(targetNode instanceof Element) || !svgNode) {
    return null
  }

  if (
    targetNode.closest(
      '[data-lugsail-theme-header="true"], [data-lugsail-theme-background="true"], #xAxis, #yAxis, #axis, [id="x axis"], [id="y axis"]'
    )
  ) {
    return null
  }

  let currentNode = targetNode
  while (currentNode && currentNode !== svgNode) {
    if (currentNode.__data__ !== undefined && currentNode.__data__ !== null) {
      return currentNode.__data__
    }
    currentNode = currentNode.parentNode
  }

  return null
}
